import { useCallback, useRef, useState } from 'react'
import { apiFetch } from '../../api/core'
import type { FinancialScenarioPayload, FinancialScenarioPreviewResponse, LineGenerationConfig, TdLine } from '../../types/testdata.types'
import type { FinancialScenarioId } from './constants'

interface ResolveArgs {
  scenarioId: FinancialScenarioId
  locale: string
  documentDate: string
  currentLines: TdLine[]
  lineGeneration: LineGenerationConfig
  setLines: (lines: TdLine[]) => void
}

/** REQ-TESTDATA-007 (CAP-003 v1.2) — Financial Scenario Preview & line-editor
 * materialization state.
 *
 * Retains NO copy of any prior line set. Every `resolve()` call is given the
 * CURRENTLY VISIBLE lines (`currentLines`, read fresh by the caller at call
 * time) as the resolver's `source_lines` input — whether those are ordinary
 * catalogue lines (the very first Normal -> Scenario transition) or an
 * already-materialized scenario's rows (a later Scenario A -> Scenario B
 * switch, or a Scenario -> Normal -> Scenario round-trip). This hook does
 * not decide which one it is and does not need to: identity borrowing is
 * idempotent by explicit backend-recorded provenance
 * (`TdLine.scenario_identity_base`, stamped by
 * `financial_scenarios.scenario_to_testdata_lines` and read back by
 * `financial_scenarios._identity_base`) — NOT by guessing from
 * `article_number`'s text — so re-deriving from an already-materialized
 * line is always safe, no matter how many times or through how many
 * different scenarios it has already been derived, and never corrupts a
 * genuine source article number that happens to already look like a
 * scenario suffix (e.g. a real article legitimately named
 * "PUMP-TRANSPORT"). There is no hidden pre-scenario "source lines" snapshot here —
 * the visible `lines` state (owned by `useLinesState`) is the ONLY line
 * state that exists, matching REQ-TESTDATA-007's explicit product decision
 * that no backup/restore of discarded ordinary lines may exist anywhere in
 * this implementation. */
export function useFinancialScenarioPreview() {
  const [preview, setPreview] = useState<FinancialScenarioPayload | null>(null)
  const [scenarioLocked, setScenarioLocked] = useState(false)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)
  // REQ-TESTDATA-007 review fix — the canonical materialization `M` is not
  // just `lines`: these document-level totals are not derivable from lines
  // alone (`document_allowance_share` is only a per-line pro-rata SHARE;
  // `prepaid_amount` is not a line property at all) and MUST be submitted
  // verbatim with the Generate request, or the backend's idempotency
  // short-circuit would silently treat them as zero/absent.
  const [documentAllowanceAmount, setDocumentAllowanceAmount] = useState<number | null>(null)
  const [documentChargeAmount, setDocumentChargeAmount] = useState<number | null>(null)
  const [prepaidAmount, setPrepaidAmount] = useState<number | null>(null)
  // Race-safety only (no line data retained here): an in-flight preview
  // response for a scenario/config the user has since navigated away from
  // (rapid switching) must never overwrite a newer selection's visible
  // rows/preview (REQ-TESTDATA-007 "Loading / error behaviour").
  const requestSeqRef = useRef(0)

  const resolve = useCallback(async ({
    scenarioId, locale, documentDate, currentLines, lineGeneration, setLines,
  }: ResolveArgs) => {
    const seq = ++requestSeqRef.current
    setPreviewLoading(true)
    setPreviewError(null)
    try {
      const body = {
        financial_scenario_id: scenarioId,
        locale,
        document_date: documentDate,
        // Always the CURRENTLY VISIBLE lines at call time — never a cached
        // snapshot. Safe whether they are raw catalogue lines or an
        // already-materialized scenario's rows (see hook docstring).
        lines: currentLines,
        line_generation: lineGeneration.mode === 'precision_test' ? lineGeneration : undefined,
      }
      const data = await apiFetch<FinancialScenarioPreviewResponse>('/api/testdata/financial-scenario-preview', {
        method: 'POST',
        body: JSON.stringify(body),
      })
      if (seq !== requestSeqRef.current) return // superseded by a newer request
      setLines(data.lines)
      setPreview(data.scenario)
      setDocumentAllowanceAmount(data.document_allowance_amount)
      setDocumentChargeAmount(data.document_charge_amount)
      setPrepaidAmount(data.prepaid_amount)
      setScenarioLocked(true)
    } catch (e: unknown) {
      if (seq !== requestSeqRef.current) return
      setPreviewError(e instanceof Error ? e.message : 'Scenario-preview mislukt')
      // REQ-TESTDATA-007 "Loading / error behaviour" — never silently switch
      // to Normal and never leave the lock in a state that would let
      // Generate proceed as though preview succeeded; `scenarioLocked`
      // stays whatever it already was (false on first selection failure,
      // true if this was a refresh of an already-successful scenario).
    } finally {
      if (seq === requestSeqRef.current) setPreviewLoading(false)
    }
  }, [])

  /** Scenario -> Normal (REQ-TESTDATA-007 explicit product decision):
   * discards the preview and unlocks. This function itself does not touch
   * `lines` — it never retains or restores any earlier line set, there is
   * nothing to restore from — but the CALLER (`useTestDataForm.clearFinancialScenario`)
   * separately dematerializes the currently-visible rows
   * (`dematerializeFinancialScenarioLine`): their user-visible values stay
   * unchanged, while scenario-only canonical/provenance metadata
   * (`net_amount_override`, `scenario_identity_base`, etc.) is cleared, so
   * they become true ordinary editable lines rather than merely
   * LOOKING editable while scenario semantics still silently apply. */
  const clear = useCallback(() => {
    requestSeqRef.current += 1 // invalidate any in-flight request
    setPreview(null)
    setScenarioLocked(false)
    setPreviewLoading(false)
    setPreviewError(null)
    setDocumentAllowanceAmount(null)
    setDocumentChargeAmount(null)
    setPrepaidAmount(null)
  }, [])

  return {
    preview, scenarioLocked, previewLoading, previewError, resolve, clear,
    documentAllowanceAmount, documentChargeAmount, prepaidAmount,
  }
}

export type FinancialScenarioPreviewState = ReturnType<typeof useFinancialScenarioPreview>
